/**
 * Phase 71 — staff-UI i18n client helpers (cookie-driven, no i18n routing).
 *
 * Source of truth for the supported locale set, the NEXT_LOCALE cookie, and the
 * client-side catalog loader used by I18nBootstrap. The same whitelist + deep-merge
 * contract is mirrored server-side in i18n/request.ts.
 */

export const SUPPORTED_LOCALES = ["en", "it"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "en";

/** next-intl convention; also read server-side in i18n/request.ts. */
export const LOCALE_COOKIE = "NEXT_LOCALE";

/** Whitelist gate (T-71-01): the ONLY value ever interpolated into a dynamic import path. */
export function isSupportedLocale(value: unknown): value is Locale {
  return (
    typeof value === "string" &&
    (SUPPORTED_LOCALES as readonly string[]).includes(value)
  );
}

/** Coerce any candidate (cookie value, DB locale, etc.) to a safe, supported locale. */
export function normalizeLocale(value: unknown): Locale {
  return isSupportedLocale(value) ? value : DEFAULT_LOCALE;
}

type Messages = Record<string, unknown>;

/**
 * Recursive deep-merge (Pitfall 4): later sources override earlier ones, but nested
 * namespace objects are merged key-by-key rather than replaced wholesale — so EN keys
 * survive inside a namespace that IT only partially translates (I18N-06).
 */
export function deepMerge<T extends Messages>(base: T, override: Messages): T {
  const out: Messages = { ...base };
  for (const key of Object.keys(override)) {
    const overrideVal = override[key];
    const baseVal = out[key];
    if (isPlainObject(baseVal) && isPlainObject(overrideVal)) {
      out[key] = deepMerge(baseVal as Messages, overrideVal as Messages);
    } else {
      out[key] = overrideVal;
    }
  }
  return out as T;
}

function isPlainObject(value: unknown): value is Messages {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
}

/** Read the NEXT_LOCALE cookie on the client (returns undefined on the server / when unset). */
export function readLocaleCookie(): Locale | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${LOCALE_COOKIE}=`));
  if (!match) return undefined;
  const raw = decodeURIComponent(match.split("=").slice(1).join("="));
  return isSupportedLocale(raw) ? raw : undefined;
}

/**
 * Write the NEXT_LOCALE cookie so the NEXT SSR paint / `<html lang>` matches the
 * chosen locale. Whitelist-validated, path=/, 1-year max-age, Lax SameSite.
 */
export function writeLocaleCookie(locale: Locale): void {
  if (typeof document === "undefined") return;
  if (!isSupportedLocale(locale)) return;
  const oneYear = 60 * 60 * 24 * 365;
  document.cookie = `${LOCALE_COOKIE}=${encodeURIComponent(
    locale
  )}; path=/; max-age=${oneYear}; SameSite=Lax`;
}

/**
 * Per-clinic "Patient" terminology override (FEATURE_PATIENTS_TERMINOLOGY). Mirrors the
 * base fallback contract: the EN override is the base layer and the locale override is
 * merged on top, so any key the locale override omits (e.g. namespaces IT hasn't
 * translated) falls back to the EN patient-version rather than reverting to "Lead".
 */
async function loadPatientsOverride(safe: Locale): Promise<Messages> {
  const en = (await import("../messages/terms/patients.en.json"))
    .default as Messages;
  if (safe === DEFAULT_LOCALE) return en;
  try {
    const loc = (await import(`../messages/terms/patients.${safe}.json`))
      .default as Messages;
    return deepMerge(structuredClone(en), loc);
  } catch {
    return en;
  }
}

/**
 * Dentx 261030 protocol-number wording (chart_number_protocol_enabled): the chart
 * number's strings say "Protocol no." instead of "Chart no.". Same EN-under-locale
 * layering as the patients override.
 */
async function loadProtocolOverride(safe: Locale): Promise<Messages> {
  const en = (await import("../messages/terms/protocol.en.json"))
    .default as Messages;
  if (safe === DEFAULT_LOCALE) return en;
  try {
    const loc = (await import(`../messages/terms/protocol.${safe}.json`))
      .default as Messages;
    return deepMerge(structuredClone(en), loc);
  } catch {
    return en;
  }
}

/**
 * Load the active catalog for a locale on the client.
 *
 * Security (T-71-01): whitelist-validate BEFORE the dynamic import — never interpolate
 * an unvalidated value into the import path. EN is deep-merged UNDER the requested locale
 * so any untranslated key falls back to English (I18N-06). When `patientsTerminology` is
 * on, a Lead→Patient override is deep-merged LAST so it wins over the base catalog.
 */
export async function loadMessages(
  locale: string,
  patientsTerminology = false,
  protocolTerms = false
): Promise<Messages> {
  const safe = normalizeLocale(locale);
  const en = (await import("../messages/en.json")).default as Messages;
  let messages: Messages =
    safe === DEFAULT_LOCALE
      ? en
      : deepMerge(
          structuredClone(en),
          (await import(`../messages/${safe}.json`)).default as Messages
        );
  if (patientsTerminology) {
    messages = deepMerge(
      structuredClone(messages),
      await loadPatientsOverride(safe)
    );
  }
  if (protocolTerms) {
    messages = deepMerge(
      structuredClone(messages),
      await loadProtocolOverride(safe)
    );
  }
  return messages;
}
